import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { Club, ClubMember, ClubJoinRequest, User } from '../../types';
import './ClubDetailView.css';

interface ClubDetailViewProps {
  clubId: number;
  user: User;
  onBack: () => void;
  onClubsChanged: () => void;
}

type Tab = 'info' | 'members' | 'requests';

interface InviteUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
}

const ClubDetailView: React.FC<ClubDetailViewProps> = ({ clubId, user, onBack, onClubsChanged }) => {
  const { t } = useLanguage();
  const [club, setClub] = useState<Club | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [requests, setRequests] = useState<ClubJoinRequest[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteResults, setInviteResults] = useState<InviteUser[]>([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  const isAdmin = club?.role === 'admin';

  const fetchClub = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setClub(data.club);
    } catch {
      setError(t('clubs.errorLoad'));
    }
  }, [clubId, t]);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/members`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMembers(data.members || []);
    } catch { /* ignore */ }
  }, [clubId]);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/requests`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRequests(data.requests || []);
    } catch { /* ignore */ }
  }, [clubId]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError(null);
      await fetchClub();
      await fetchMembers();
      setLoading(false);
    };
    loadAll();
  }, [fetchClub, fetchMembers]);

  useEffect(() => {
    if (activeTab === 'requests' && isAdmin) {
      fetchRequests();
    }
  }, [activeTab, isAdmin, fetchRequests]);

  const handleLeave = async () => {
    if (!window.confirm(t('clubs.leaveConfirm'))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/leave`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        onClubsChanged();
        onBack();
      }
    } catch { /* ignore */ }
  };

  const handleMemberAction = async (memberId: number, action: 'promote' | 'demote' | 'remove') => {
    if (action === 'remove' && !window.confirm(t('clubs.removeConfirm'))) return;
    setActionLoading(memberId);
    try {
      const endpoint = action === 'promote'
        ? `${API_BASE_URL}/api/clubs/${clubId}/members/${memberId}/promote`
        : action === 'demote'
        ? `${API_BASE_URL}/api/clubs/${clubId}/members/${memberId}/demote`
        : `${API_BASE_URL}/api/clubs/${clubId}/members/${memberId}`;

      const method = action === 'remove' ? 'DELETE' : 'POST';
      const res = await fetch(endpoint, { method, credentials: 'include' });
      if (res.ok) {
        await fetchMembers();
      }
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleRequestAction = async (requestId: number, action: 'approve' | 'reject') => {
    setActionLoading(requestId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/requests/${requestId}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchRequests();
        await fetchMembers();
        onClubsChanged();
      }
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  // Invite: search users
  useEffect(() => {
    if (!showInvite || inviteQuery.length < 2) {
      setInviteResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/clubs/${clubId}/search-users?q=${encodeURIComponent(inviteQuery)}`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          setInviteResults(data.users || []);
        }
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [inviteQuery, showInvite, clubId]);

  const handleInvite = async (targetUser: InviteUser) => {
    setInviteLoading(true);
    setInviteMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: targetUser.username }),
      });
      const data = await res.json();
      if (res.ok) {
        setInviteMsg(data.status === 'approved' ? t('clubs.inviteApproved') : t('clubs.inviteSuccess'));
        setInviteQuery('');
        setInviteResults([]);
        await fetchMembers();
        onClubsChanged();
      } else {
        const errKey = data.error === 'User is already a member' ? 'inviteAlreadyMember'
          : data.error === 'User already has a pending invitation' ? 'inviteAlreadyInvited'
          : 'inviteError';
        setInviteMsg(t(`clubs.${errKey}`));
      }
    } catch {
      setInviteMsg(t('clubs.inviteError'));
    }
    setInviteLoading(false);
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
      const tz = club?.timezone || undefined;
      return d.toLocaleDateString(undefined, { timeZone: tz });
    } catch {
      return dateStr;
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="club-detail-loading">
        <div className="club-detail-skeleton" />
        <div className="club-detail-skeleton short" />
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="club-detail-error">
        <button className="club-detail-back" onClick={onBack}>← {t('common.back')}</button>
        <p>{error || t('clubs.errorLoad')}</p>
      </div>
    );
  }

  return (
    <div className="club-detail">
      <div className="club-detail-header">
        <button className="club-detail-back" onClick={onBack}>← {t('common.back')}</button>
        <div className="club-detail-info">
          <div className="club-detail-title-row">
            <h1 className="club-detail-name">{club.name}</h1>
            {club.short_name && <span className="club-detail-short">{club.short_name}</span>}
          </div>
          <div className="club-detail-meta">
            <span>{t('clubs.memberCount').replace('{{count}}', String(club.member_count))}</span>
            {club.role && (
              <span className={`club-role-badge club-role-${club.role}`}>
                {t(`clubs.${club.role}`)}
              </span>
            )}
          </div>
        </div>
        <button className="club-detail-leave" onClick={handleLeave}>
          {t('clubs.leaveClub')}
        </button>
      </div>

      <div className="club-detail-tabs">
        <button
          className={`club-detail-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          {t('clubs.info')}
        </button>
        <button
          className={`club-detail-tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          {t('clubs.members')}
        </button>
        {isAdmin && (
          <button
            className={`club-detail-tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            {t('clubs.requests')}
            {club.pending_request_count && club.pending_request_count > 0 ? (
              <span className="club-tab-badge">{club.pending_request_count}</span>
            ) : null}
          </button>
        )}
      </div>

      <div className="club-detail-content">
        {activeTab === 'info' && (
          <div className="club-info-tab">
            {club.description && (
              <div className="club-info-row">
                <p className="club-info-description">{club.description}</p>
              </div>
            )}
            <div className="club-info-row">
              <span className="club-info-label">Timezone:</span>
              <span>{club.timezone}</span>
            </div>
            <div className="club-info-row">
              <span className="club-info-label">{t('clubs.joined')}:</span>
              <span>{formatDate(club.created_at)}</span>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="club-members-tab">
            {isAdmin && (
              <div className="club-invite-section">
                <button
                  className="club-invite-toggle"
                  onClick={() => { setShowInvite(!showInvite); setInviteMsg(null); setInviteQuery(''); setInviteResults([]); }}
                >
                  {showInvite ? '✕' : '+'} {t('clubs.inviteMember')}
                </button>
                {showInvite && (
                  <div className="club-invite-panel">
                    <input
                      type="text"
                      className="club-invite-input"
                      placeholder={t('clubs.inviteSearch')}
                      value={inviteQuery}
                      onChange={e => setInviteQuery(e.target.value)}
                      autoFocus
                    />
                    {inviteMsg && <p className="club-invite-msg">{inviteMsg}</p>}
                    {inviteResults.length > 0 && (
                      <div className="club-invite-results">
                        {inviteResults.map(u => (
                          <div key={u.id} className="club-invite-row">
                            <div className="club-invite-user">
                              {u.profile_picture ? (
                                <img src={u.profile_picture} alt="" className="club-member-avatar" />
                              ) : (
                                <div className="club-member-initials">
                                  {getInitials(u.first_name, u.last_name)}
                                </div>
                              )}
                              <span>{u.first_name} {u.last_name}</span>
                              <span className="club-member-username">@{u.username}</span>
                            </div>
                            <button
                              className="club-action-btn club-action-btn-approve"
                              onClick={() => handleInvite(u)}
                              disabled={inviteLoading}
                            >
                              {t('clubs.inviteBtn')}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {inviteQuery.length >= 2 && inviteResults.length === 0 && (
                      <p className="club-invite-no-results">{t('clubs.inviteNoResults')}</p>
                    )}
                  </div>
                )}
              </div>
            )}
            <table className="club-members-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>@</th>
                  <th>{t('clubs.role')}</th>
                  <th>{t('clubs.joined')}</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id}>
                    <td>
                      {m.profile_picture ? (
                        <img
                          src={m.profile_picture}
                          alt={m.first_name}
                          className="club-member-avatar"
                        />
                      ) : (
                        <div className="club-member-initials">
                          {getInitials(m.first_name, m.last_name)}
                        </div>
                      )}
                    </td>
                    <td>{m.first_name} {m.last_name}</td>
                    <td className="club-member-username">@{m.username}</td>
                    <td>
                      <span className={`club-role-badge club-role-${m.role}`}>
                        {t(`clubs.${m.role}`)}
                      </span>
                    </td>
                    <td className="club-member-date">{formatDate(m.joined_at)}</td>
                    {isAdmin && m.user_id !== user.id && (
                      <td>
                        <div className="club-member-actions">
                          {m.role === 'member' && (
                            <button
                              className="club-action-btn"
                              onClick={() => handleMemberAction(m.user_id, 'promote')}
                              disabled={actionLoading === m.user_id}
                            >
                              {t('clubs.promote')}
                            </button>
                          )}
                          {m.role === 'admin' && (
                            <button
                              className="club-action-btn"
                              onClick={() => handleMemberAction(m.user_id, 'demote')}
                              disabled={actionLoading === m.user_id}
                            >
                              {t('clubs.demote')}
                            </button>
                          )}
                          <button
                            className="club-action-btn club-action-btn-danger"
                            onClick={() => handleMemberAction(m.user_id, 'remove')}
                            disabled={actionLoading === m.user_id}
                          >
                            {t('clubs.remove')}
                          </button>
                        </div>
                      </td>
                    )}
                    {isAdmin && m.user_id === user.id && <td />}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'requests' && isAdmin && (
          <div className="club-requests-tab">
            {requests.length === 0 ? (
              <p className="club-no-requests">{t('clubs.noRequests')}</p>
            ) : (
              <div className="club-requests-list">
                {requests.map(req => (
                  <div key={req.id} className="club-request-row">
                    <div className="club-request-info">
                      <span className="club-request-name">{req.first_name} {req.last_name}</span>
                      <span className="club-request-username">@{req.username}</span>
                      {req.message && (
                        <span className="club-request-message">{req.message}</span>
                      )}
                      <span className="club-request-date">{formatDate(req.created_at)}</span>
                    </div>
                    <div className="club-request-actions">
                      <button
                        className="club-action-btn club-action-btn-approve"
                        onClick={() => handleRequestAction(req.id, 'approve')}
                        disabled={actionLoading === req.id}
                      >
                        {t('clubs.approve')}
                      </button>
                      <button
                        className="club-action-btn club-action-btn-danger"
                        onClick={() => handleRequestAction(req.id, 'reject')}
                        disabled={actionLoading === req.id}
                      >
                        {t('clubs.reject')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubDetailView;
